import { Button, Popover, SimpleGrid, type ButtonProps } from '@chakra-ui/react';
import { type MouseEvent, type ReactNode } from 'react';

const SmileyIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M8 14s1.5 2 4 2 4-2 4-2" />
    <circle cx="9" cy="9.5" r="1" fill="currentColor" stroke="none" />
    <circle cx="15" cy="9.5" r="1" fill="currentColor" stroke="none" />
  </svg>
);

const EMOJIS = [
  '😀',
  '😁',
  '😂',
  '😊',
  '😉',
  '😍',
  '😘',
  '😎',
  '🤝',
  '👍',
  '👋',
  '🙏',
  '🔥',
  '⭐',
  '💯',
  '🎮',
  '🏆',
  '🚀',
  '😅',
  '🤔',
  '😴',
  '😮',
  '😡',
  '❤️',
];

type EmojiPickerProps = {
  disabled?: boolean;
  variant?: 'outline' | 'ghost';
  color?: string;
  children?: ReactNode;
  onSelect: (emoji: string) => void;
  /**
   * Не забирать фокус у того, что было в фокусе до открытия.
   *
   * Нужно там, где рядом стоит поле ввода: на телефоне фокус — это ещё и экранная клавиатура,
   * и обычное поведение (нажатие переводит фокус на кнопку, открытое окошко забирает его себе)
   * роняет её вниз. Тогда после выбора смайлика человек тапает по полю заново.
   *
   * Где поля рядом нет — реакции на сообщение, реакции в комментариях, — включать незачем:
   * без этого окошко само ставит фокус внутрь себя, и с клавиатуры оно доступнее.
   */
  keepsFocus?: boolean;
} & Pick<
  ButtonProps,
  '_hover' | '_active' | '_focus' | 'ml' | 'minW' | 'h' | 'borderRadius' | 'size'
>;

export const EmojiPicker = ({
  disabled = false,
  variant = 'outline',
  color,
  children,
  onSelect,
  keepsFocus = false,
  ...buttonProps
}: EmojiPickerProps) => {
  // Фокус остаётся там, где был: браузер переводит его на нажатую кнопку как действие по
  // умолчанию у `mousedown`, и отмена этого действия клику не мешает. Нужно и на самой кнопке
  // окошка, и на каждом смайлике внутри — иначе фокус (а с ним клавиатура) уходит на выборе.
  const holdFocus = keepsFocus ? (event: MouseEvent) => event.preventDefault() : undefined;

  return (
  <Popover.Root
    positioning={{ placement: 'top-start' }}
    lazyMount
    unmountOnExit
    autoFocus={!keepsFocus}
  >
    <Popover.Trigger asChild>
      <Button
        type="button"
        onMouseDown={holdFocus}
        size="sm"
        minW="40px"
        p={0}
        variant={variant}
        borderColor={variant === 'outline' ? 'whiteAlpha.300' : 'transparent'}
        disabled={disabled}
        color={color}
        aria-label="Pick emoji"
        focusRing="none"
        {...buttonProps}
      >
        {children ?? <SmileyIcon />}
      </Button>
    </Popover.Trigger>
    <Popover.Positioner zIndex="popover">
      <Popover.Content borderColor="border" bg="bg.glass.raised" w="260px">
        <Popover.Body p={2}>
          <SimpleGrid columns={8} gap={1}>
            {EMOJIS.map((emoji) => (
              <Button
                key={emoji}
                type="button"
                h="32px"
                minW="0"
                p={0}
                variant="ghost"
                fontSize="lg"
                onClick={() => onSelect(emoji)}
                onMouseDown={holdFocus}
                _hover={{ bg: 'bg.muted' }}
              >
                {emoji}
              </Button>
            ))}
          </SimpleGrid>
        </Popover.Body>
      </Popover.Content>
    </Popover.Positioner>
  </Popover.Root>
  );
};
